import { Box, Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { useRetrieveTicketQuery } from '@sophocles/features/entities/tickets';

import TicketResolvedChip from './components/TicketResolvedChip';

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionCard({ title, children, sx }) {
  return (
    <Card sx={{ mb: 2, ...sx }}>
      <CardContent>
        {title ? (
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {title}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

export default function TicketView(props) {
  const { t } = useTranslation();
  const { ticket_id: ticketId } = useParams();

  const { data: ticket, isLoading, error } = useRetrieveTicketQuery({ id: ticketId }, { skip: !ticketId });
  const { isStaff } = useUser();

  const customer = ticket?.customer ?? null;
  const siteGroup = ticket?.site_group ?? null;
  const site = ticket?.site ?? null;
  const issue = ticket?.issue ?? null;
  const contact = ticket?.new_contact ?? null;
  const createdBy = ticket?.created_by ?? null;
  const ticketNumber = ticket?.ticket_number ?? '';
  const createdTime = ticket?.created_time ?? null;
  const reportedBy = ticket?.reported_by ?? '';
  const problemDescription = ticket?.problem_description ?? '';
  const resolution = ticket?.resolution ?? '';
  const internalNotes = ticket?.internal_notes ?? '';

  const pageTitle = ticketNumber
    ? `${t('pages.viewSupportTicket', 'View Ticket')} - ${ticketNumber}`
    : t('pages.viewSupportTicket', 'View Ticket');

  const contactName = _.get(contact, 'name', '') || _.get(contact, 'business_name', '');
  const contactBusiness = _.get(contact, 'business_name', '');
  const contactEmail = _.get(contact, 'email', '');
  const contactPhone = _.get(contact, 'phone1', '');
  const hasContact = Boolean(contact && (contactName || contactEmail || contactPhone));

  if (isLoading) {
    return (
      <AppNav page="plant_tickets" notFullHeight {...props}>
        <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
          <TitleBar showBackButton title={pageTitle} />
          <LoadingScreen />
        </Box>
      </AppNav>
    );
  }

  if (error) {
    return (
      <AppNav page="plant_tickets" notFullHeight {...props}>
        <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
          <TitleBar showBackButton title={pageTitle} />
          <Typography color="error">{t('tickets.ticketLoadFailed', 'Failed to load ticket')}</Typography>
        </Box>
      </AppNav>
    );
  }

  return (
    <AppNav page="plant_tickets" notFullHeight {...props}>
      <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
        <TitleBar showBackButton title={pageTitle} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <TicketResolvedChip resolution={resolution} />
          {createdBy ? (
            <Typography variant="body2" color="text.secondary">
              {t('general.createdBy', 'Created by')} {_.get(createdBy, 'name', '')}
              {createdTime ? ` · ${moment(createdTime).fromNow()}` : ''}
            </Typography>
          ) : null}
        </Stack>

        <Grid container spacing={2}>
          {/* Main column */}
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionCard title={t('tickets.problemDescription', 'Problem Description')}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {problemDescription || (
                  <Typography component="span" variant="body2" color="text.disabled">
                    —
                  </Typography>
                )}
              </Typography>
            </SectionCard>

            <SectionCard title={t('tickets.nameOfCaller', 'Name of Caller')}>
              <Typography variant="body2" sx={{ mb: hasContact ? 2 : 0 }}>
                {reportedBy || (
                  <Typography component="span" variant="body2" color="text.disabled">
                    —
                  </Typography>
                )}
              </Typography>
              {hasContact ? (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('general.contact', 'Contact')}
                  </Typography>
                  <DetailRow
                    label={t('general.name', 'Name')}
                    value={
                      contactName
                        ? contactName +
                          (contactName && contactBusiness && contactName !== contactBusiness
                            ? ` (${contactBusiness})`
                            : '')
                        : ''
                    }
                  />
                  <DetailRow label={t('general.email', 'Email')} value={contactEmail} />
                  <DetailRow label={t('general.phone', 'Phone')} value={contactPhone} />
                </>
              ) : null}
            </SectionCard>

            {resolution ? (
              <SectionCard title={t('tickets.resolution', 'Resolution')}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {resolution}
                </Typography>
              </SectionCard>
            ) : null}

            {isStaff ? (
              <SectionCard title={t('tickets.internalComments', 'Internal Comments')}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {internalNotes || (
                    <Typography component="span" variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </Typography>
              </SectionCard>
            ) : null}
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <SectionCard title={t('general.details', 'Details')}>
              <DetailRow label={t('tickets.ticketNumber', 'Ticket Number')} value={ticketNumber || ''} />
              {isStaff && customer ? (
                <DetailRow
                  label={t('general.customer', 'Customer')}
                  value={`${_.get(customer, 'id', '')}. ${_.get(customer, 'name', '')}`.trim()}
                />
              ) : null}
              {siteGroup ? (
                <DetailRow
                  label={t('general.plant', 'Plant')}
                  value={[_.get(siteGroup, 'name'), _.get(siteGroup, 'description')].filter(Boolean).join(' — ')}
                />
              ) : null}
              {site ? <DetailRow label={t('general.site', 'Site')} value={_.get(site, 'name', '')} /> : null}
              {issue ? (
                <>
                  <DetailRow label={t('tickets.category', 'Category')} value={_.get(issue, ['category', 'name'], '')} />
                  <DetailRow label={t('tickets.subCategory', 'Sub Category')} value={_.get(issue, 'name', '')} />
                </>
              ) : null}
              {createdBy ? (
                <DetailRow label={t('general.createdBy', 'Created by')} value={_.get(createdBy, 'name', '')} />
              ) : null}
              {createdTime ? (
                <DetailRow label={t('general.created', 'Created')} value={moment(createdTime).format('LLL')} />
              ) : null}
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
    </AppNav>
  );
}
